import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import {
  TrendingDown,
  Flame,
  AlertTriangle,
  Users,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Zap,
  Clock,
  Shield,
  FileText,
} from "lucide-react";
import { Link } from "wouter";
import { formatDistanceToNow } from "date-fns";

interface DecayUser {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  grade: number;
  profilePhotoUrl?: string;
  totalXP: number;
  effectivePoints: number;
  inactiveDays: number;
  activityFactor: number;
  decayLoss: number;
  lastActiveAt: string;
  subscriptionStatus: string;
}

interface DecaySummary {
  totalStudents: number;
  activeCount: number;
  decayingCount: number;
  criticalCount: number;
  totalXPLost: number;
  avgInactiveDays: number;
}

interface DecayResponse {
  users: DecayUser[];
  summary: DecaySummary;
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

function getStatusInfo(inactiveDays: number, activityFactor: number) {
  if (inactiveDays <= 3) return { label: "Active", variant: "default" as const, color: "text-green-600 dark:text-green-400" };
  if (inactiveDays <= 7) return { label: "Cooling Off", variant: "secondary" as const, color: "text-yellow-600 dark:text-yellow-400" };
  if (inactiveDays <= 14) return { label: "Decaying", variant: "outline" as const, color: "text-orange-600 dark:text-orange-400" };
  return { label: "Critical", variant: "destructive" as const, color: "text-red-600 dark:text-red-400" };
}

export default function AdminXPDecay() {
  const [gradeFilter, setGradeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("inactiveDays");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  const queryParams = new URLSearchParams({
    grade: gradeFilter,
    status: statusFilter,
    sort: sortField,
    order: sortOrder,
    page: page.toString(),
    limit: "30",
  });

  const { data, isLoading } = useQuery<DecayResponse>({
    queryKey: ['/api/admin/xp-decay', { grade: gradeFilter, status: statusFilter, sort: sortField, order: sortOrder, page }],
    queryFn: async () => {
      const res = await fetch(`/api/admin/xp-decay?${queryParams}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch XP decay data');
      return res.json();
    },
  });

  const summary = data?.summary;
  const users = data?.users || [];
  const totalPages = data?.totalPages || 1;

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 ml-1 opacity-40" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-primary" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1 text-primary" />;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">XP Decay Monitor</h1>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">XP Decay Monitor</h1>
        <p className="text-muted-foreground mt-1">
          Track student inactivity and XP decay across the platform. Students lose XP after 3 days of inactivity.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-medium">Total Students</span>
            </div>
            <p className="text-2xl font-bold">{summary?.totalStudents || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Flame className="w-4 h-4 text-green-500" />
              <span className="text-xs text-muted-foreground font-medium">Active</span>
            </div>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{summary?.activeCount || 0}</p>
            {summary && summary.totalStudents > 0 && (
              <Progress value={(summary.activeCount / summary.totalStudents) * 100} className="h-1.5 mt-2" />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-muted-foreground font-medium">Decaying</span>
            </div>
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{summary?.decayingCount || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-xs text-muted-foreground font-medium">Critical</span>
            </div>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{summary?.criticalCount || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-muted-foreground font-medium">Total XP Lost</span>
            </div>
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{(summary?.totalXPLost || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-medium">Avg Inactive Days</span>
            </div>
            <p className="text-2xl font-bold">{summary?.avgInactiveDays || 0}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 p-2 rounded-lg bg-muted/30">
            <div>
              <span className="text-xs text-muted-foreground mb-1 block">0-3 days</span>
              <div className="flex items-center gap-2">
                <div className="h-3 rounded-full bg-green-500 flex-1" style={{ maxWidth: `${summary ? Math.max(10, (summary.activeCount / summary.totalStudents) * 100) : 10}%` }} />
                <span className="text-xs font-medium text-green-600 dark:text-green-400">No decay</span>
              </div>
            </div>
            <div>
              <span className="text-xs text-muted-foreground mb-1 block">4-7 days</span>
              <div className="flex items-center gap-2">
                <div className="h-3 rounded-full bg-yellow-500 flex-1" style={{ maxWidth: '60%' }} />
                <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400">2%/day</span>
              </div>
            </div>
            <div>
              <span className="text-xs text-muted-foreground mb-1 block">8-14 days</span>
              <div className="flex items-center gap-2">
                <div className="h-3 rounded-full bg-orange-500 flex-1" style={{ maxWidth: '40%' }} />
                <span className="text-xs font-medium text-orange-600 dark:text-orange-400">3%/day</span>
              </div>
            </div>
            <div>
              <span className="text-xs text-muted-foreground mb-1 block">15+ days</span>
              <div className="flex items-center gap-2">
                <div className="h-3 rounded-full bg-red-500 flex-1" style={{ maxWidth: '25%' }} />
                <span className="text-xs font-medium text-red-600 dark:text-red-400">5%/day (min 25%)</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={gradeFilter} onValueChange={(v) => { setGradeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Grade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Grades</SelectItem>
            <SelectItem value="4">Grade 4</SelectItem>
            <SelectItem value="5">Grade 5</SelectItem>
            <SelectItem value="6">Grade 6</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active (0-3 days)</SelectItem>
            <SelectItem value="decaying">Decaying (4+ days)</SelectItem>
            <SelectItem value="critical">Critical (15+ days)</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto text-sm text-muted-foreground">
          Showing {users.length} of {data?.totalCount || 0} students
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[250px]">Student</TableHead>
                  <TableHead className="w-[80px]">Grade</TableHead>
                  <TableHead>
                    <button onClick={() => handleSort('inactiveDays')} className="flex items-center font-medium hover:text-primary transition-colors">
                      Inactive Days <SortIcon field="inactiveDays" />
                    </button>
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>
                    <button onClick={() => handleSort('totalXP')} className="flex items-center font-medium hover:text-primary transition-colors">
                      Earned XP <SortIcon field="totalXP" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button onClick={() => handleSort('effectivePoints')} className="flex items-center font-medium hover:text-primary transition-colors">
                      Effective XP <SortIcon field="effectivePoints" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button onClick={() => handleSort('decayLoss')} className="flex items-center font-medium hover:text-primary transition-colors">
                      XP Lost <SortIcon field="decayLoss" />
                    </button>
                  </TableHead>
                  <TableHead>Decay %</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead>Sub Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => {
                  const statusInfo = getStatusInfo(user.inactiveDays, user.activityFactor);
                  const decayPercent = Math.round((1 - user.activityFactor) * 100);
                  return (
                    <TableRow key={user.id} className={user.inactiveDays > 14 ? 'bg-red-50/50 dark:bg-red-950/10' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.profilePhotoUrl || ''} alt={`${user.firstName} ${user.lastName}`} />
                            <AvatarFallback className="bg-primary/10 text-primary text-xs">
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{user.grade}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`font-semibold ${statusInfo.color}`}>
                          {user.inactiveDays}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{user.totalXP.toLocaleString()}</TableCell>
                      <TableCell className="font-mono text-sm font-semibold">{user.effectivePoints.toLocaleString()}</TableCell>
                      <TableCell>
                        {user.decayLoss > 0 ? (
                          <span className="text-red-600 dark:text-red-400 font-mono text-sm">-{user.decayLoss.toLocaleString()}</span>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {decayPercent > 0 ? (
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="flex items-center gap-1.5">
                                <div className="w-16 bg-muted rounded-full h-2 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${decayPercent > 50 ? 'bg-red-500' : decayPercent > 20 ? 'bg-orange-500' : 'bg-yellow-500'}`}
                                    style={{ width: `${Math.min(100, (decayPercent / 75) * 100)}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium">{decayPercent}%</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Activity factor: {user.activityFactor.toFixed(2)}</p>
                              <p>Max decay: 75% (25% floor)</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <Shield className="w-3.5 h-3.5 text-green-500" />
                            <span className="text-xs text-green-600 dark:text-green-400">Protected</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {user.lastActiveAt ? formatDistanceToNow(new Date(user.lastActiveAt), { addSuffix: true }) : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.subscriptionStatus === 'active' ? 'default' : 'secondary'} className="text-xs">
                          {user.subscriptionStatus || 'none'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Link href={`/admin/user/${user.id}/report`}>
                          <Button variant="ghost" size="sm">
                            <FileText className="w-4 h-4 mr-1" />
                            Report
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-12 text-muted-foreground">
                      No students found matching the selected filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground px-3">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
